/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useState } from "react";
import {
  Box,
  Button,
  FlexContainer,
  ModalConfirm,
  theme,
} from "@nordcloud/gnui";
import {
  useGetApiKeysPermissionsQuery,
  useRegenerateAccessKeySecretMutation,
  useRemoveApiKeyPermissionsMutation,
} from "~/generated/graphql";
import {
  ACTION,
  BreadcrumbsBox,
  Pagination,
  ReactTable,
  TableLoader,
  UniversalWrap,
} from "~/components";
import { ERROR_TEXT } from "~/constants";
import { useQueryState } from "~/hooks";
import { showError } from "~/services/toast";
import { SearchSortContainer } from "../components";
import { PageName, SELECT_OPTIONS_GLOBAL_KEYS_APIS } from "../constants";
import { useSettingsSort } from "../hook";
import { QuerySort } from "../types";
import { UpdateApiKeyInput } from "../validators/ApiKeys";
import { AddApiKeySidebar } from "./AddApiKeySidebar";
import { ApiKeysListColumns } from "./ApiKeysListColumns";
import { ApiKeySuccessModal } from "./ApiKeySuccessModal";
import { mapApiKeyPermissions } from "./mapping";
import { text } from "./text";

type ApiKeyInfo = { id: string; description?: string };

type ConfirmModal = {
  actionName: ACTION;
  action: (id: string) => void;
  actionLabel: string;
  contentLabel: string;
  content: JSX.Element | string;
};

export function ApiKeysTable() {
  const { state, updateQueryState } = useQueryState();
  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const { page = 0, limit = 20, field, order, query }: QuerySort = state;

  const [searchInput, setSearchInput] = useState(query);

  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    useSettingsSort({
      field,
      order,
      page,
      pageName: PageName.GlobalApiKeys,
      // @ts-expect-error ignored temporary after migration to TypeScript 4.9
      updateQueryState,
    });

  const [confirmationModal, setConfirmationModal] =
    useState<ConfirmModal | null>(null);
  const [rotatedNewSecret, setRotatedNewSecret] = useState<string | null>(null);

  const [selectedApiKeyInfo, setSelectedApiKeyInfo] = useState<ApiKeyInfo>({
    id: "",
    description: "",
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editItem, setEditItem] = useState<UpdateApiKeyInput | null>(null);

  const [rotate] = useRegenerateAccessKeySecretMutation();

  const {
    data: apiKeysPermissions,
    loading,
    error,
    refetch,
  } = useGetApiKeysPermissionsQuery({
    variables: {
      limit,
      page,
      sortByValue: field,
      sortByOrder: order,
      searchByValue: searchInput,
    },
  });

  const [removeAPIKey] = useRemoveApiKeyPermissionsMutation();

  const onRemoveConfirmationModal = (apiKeyInfo: ApiKeyInfo) => {
    setSelectedApiKeyInfo(apiKeyInfo);
    setConfirmationModal({
      actionName: ACTION.DELETE,
      action: onRemoveAPIKey,
      actionLabel: text.deleteApiKey.warning.button,
      contentLabel: text.deleteApiKey.warning.caption,
      content: text.deleteApiKey.warning.areYouSure(
        apiKeyInfo.id,
        apiKeyInfo.description
      ),
    });
  };

  const onRemoveAPIKey = async (id: string) => {
    try {
      await removeAPIKey({
        variables: {
          id,
        },
      });
      await refetch();
    } catch {
      showError(ERROR_TEXT.default);
    }

    setConfirmationModal(null);
    setSelectedApiKeyInfo({ id: "", description: "" });
  };

  const onEditApiKey = (item: UpdateApiKeyInput) => {
    setIsEdit(true);
    setEditItem(item);
    setIsSidebarOpen(true);
  };

  const onRotateKey = ({
    id,
    description,
  }: {
    id: string;
    description: string;
  }) => {
    const content = (
      <FlexContainer
        direction="column"
        alignItems="flex-start"
        rowGap={theme.spacing.spacing02}
      >
        <p>{text.rotateApiKey.warning.text}</p>
        <p>{text.rotateApiKey.warning.areYouSure(id, description)}</p>
      </FlexContainer>
    );

    setSelectedApiKeyInfo({ id });
    setConfirmationModal({
      actionName: ACTION.CUSTOM_TRIGGER,
      action: rotateApiKey,
      actionLabel: text.rotateApiKey.warning.button,
      contentLabel: text.rotateApiKey.warning.caption,
      content,
    });
  };

  const rotateApiKey = async (id: string) => {
    setConfirmationModal(null);

    try {
      const response = await rotate({
        variables: {
          id,
        },
      });

      if (response.data?.regenerateAccessKeySecret) {
        setRotatedNewSecret(response.data?.regenerateAccessKeySecret?.secret);
      } else {
        return showError(ERROR_TEXT.tryAgainDefault);
      }

      await refetch();
    } catch {
      showError(ERROR_TEXT.tryAgainDefault);
    }
  };

  const columns = useMemo(
    () =>
      ApiKeysListColumns(onRemoveConfirmationModal, onRotateKey, onEditApiKey),
    []
  );

  const rows = useMemo(
    () =>
      apiKeysPermissions?.accessKeysPermissions?.keyPermissions?.map((record) =>
        mapApiKeyPermissions(record)
      ) ?? [],
    [apiKeysPermissions]
  );

  return (
    <>
      <BreadcrumbsBox
        title="Global API Keys"
        labels={[
          {
            value: "global-api-keys",
            label: "Global API Keys",
          },
        ]}
      >
        <Button
          style={{ marginBottom: theme.spacing.spacing01 }}
          icon="plus"
          onClick={() => {
            setIsEdit(false);
            setEditItem(null);
            setIsSidebarOpen(true);
          }}
        >
          Add New API Key
        </Button>
      </BreadcrumbsBox>
      <SearchSortContainer
        placeHolder="e.g., API Key Description, ID"
        sortOptions={SELECT_OPTIONS_GLOBAL_KEYS_APIS}
        sortField={sortField}
        sortDirection={sortDirection}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSortSelect={onSortSelect}
        onSortOrderChange={onSortOrderChange}
      />
      <Box spacing="spacing04">
        <UniversalWrap
          errorProps={{ error }}
          loaderProps={{
            loading,
            Component: Loader,
          }}
        >
          <ReactTable striped columns={columns} data={rows} sort={false} />
          <Pagination
            count={apiKeysPermissions?.accessKeysPermissions?.count ?? 0}
          />
        </UniversalWrap>
      </Box>
      <ModalConfirm
        isOpen={!!confirmationModal}
        confirm={() => confirmationModal?.action(selectedApiKeyInfo.id)}
        actionLabel={confirmationModal?.actionLabel ?? ""}
        contentLabel={confirmationModal?.contentLabel ?? ""}
        onClose={() => setConfirmationModal(null)}
      >
        {confirmationModal?.content}
      </ModalConfirm>
      {rotatedNewSecret && (
        <ApiKeySuccessModal
          isOpen={!!rotatedNewSecret}
          data={{
            keyId: "",
            keySecret: rotatedNewSecret,
            open: !!rotatedNewSecret,
          }}
          onClose={() => setRotatedNewSecret(null)}
        />
      )}
      <AddApiKeySidebar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        refetch={refetch}
        edit={isEdit}
        editItem={editItem}
      />
    </>
  );
}

function Loader() {
  return (
    <TableLoader
      gapY={10}
      gapX={10}
      css={{
        marginTop: theme.spacing.spacing04,
      }}
      rows={[
        {
          height: 20,
          count: 1,
          items: [
            {
              widthPercent: 34,
            },
            {
              widthPercent: 12,
            },
            {
              widthPercent: 29,
            },
            {
              widthPercent: 15,
            },
            {
              widthPercent: 10,
            },
          ],
        },
        {
          height: 60,
          count: 9,
          items: [
            {
              widthPercent: 34,
            },
            {
              widthPercent: 12,
            },
            {
              widthPercent: 29,
            },
            {
              widthPercent: 15,
            },
            {
              widthPercent: 10,
            },
          ],
        },
      ]}
    />
  );
}
